import React, { useCallback, useEffect, useState } from "react";
import { map, sortBy, filter } from "lodash";
import axios from "axios";
import classNames from "classnames";
import momentTZ from "moment-timezone";
import { Popconfirm, Tooltip, notification } from "antd";
import Icon, { CloseOutlined, QuestionCircleOutlined } from "@ant-design/icons";

// import local dependencies
import TableWithDynamicPagination from "../table/table";
import WellBeEMPIService from "@services/empiService.service";
import sessionService from "@services/session.service";
import ExtraDetailsOfUser, { ExtraDetailsOfUserComparison } from "../interventionCases/detailsOfUser/detailsOfUser";
import { formatDisplayDate } from "@utils/helpers";
import "./pendingProcessing.scss";

const PendingProcessing = (props: { activeTab: any }) => {
  const [columnList, setColumnList] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cancelledPendingProcessingIds, setCancelledPendingProcessingIds] = useState<any>([]);
  const [columnsNeedToShowInTable, setColumnsNeedToShowInTable] = useState<any>([]);
  const [columnsNeedToShowInPopUp, setColumnsNeedToShowInPopUp] = useState<any>([]);
  const [disableCancelButton, setDisableCancelButton] = useState<any>("");
  const [lastRefreshDate, setLastRefreshDate] = useState<any>(undefined);
  const [dayOfBatchProcessing, setDayOfBatchProcessing] = useState<any>(undefined);
  const [fromHour, setFromHour] = useState<any>(undefined);
  const [toHour, setToHour] = useState<any>(undefined);

  const fetchData = async (payload: any) => {
    console.log("PendingProcessing.fetchData");
    checkBatchProcessingStatus();
    setCancelledPendingProcessingIds([]);

    const _data = await axios.post(`${sessionService.getClient()?.appServiceUrl}/empi/pending-processing-details`, { ...payload }).then((response) => {
      return response.data.data.records;
    });
    return {
      totalCount: _data?.count,
      data: map(_data?.pendingProcessingRecords, (item) => {
        return { ...item, ...item?.source };
      }),
    };
  };

  const cancelOnGoingProcessing = useCallback(
    (uniqueId: any) => {
      setLoading(true);
      WellBeEMPIService.createNewEmpi({
        sourceId: uniqueId,
        targetId: null,
        action: "cancel",
      }).subscribe({
        next: (v) => {
          setCancelledPendingProcessingIds([...cancelledPendingProcessingIds, uniqueId]);
          notification.success({
            message: "Success",
            description: "Cancelled the Pending Processing Procedure.",
            placement: "top",
          });
          setLoading(false);
        },
        error: (e) => {
          console.log(e);
          setLoading(false);
        },
      });
    },
    [cancelledPendingProcessingIds]
  );

  useEffect(() => {
    let _columns = map(columnsNeedToShowInTable, (item) => {
      if (item.key === "pred_empi") {
        return {
          title: item.tableHeaderName,
          dataIndex: item.dataIndex,
          key: item.key,
          ellipsis: true,
          width: 350,
          render: (value: any, record: any) => {
            return record.action_description?.toLowerCase()?.includes("merg") ? (
              <div>
                {value}
                <div className="ui-empi-matching-records__table__file-column">{record.file_name}</div>
              </div>
            ) : (
              <div className="ui-empi-matching-records__table__file-column">{record.file_name}</div>
            );
          },
        };
      } else if (item.key === "member_dob") {
        return { title: item.tableHeaderName, dataIndex: item.dataIndex, key: item.key, sorter: true, render: (value: any) => formatDisplayDate(value, "createDateFormat", "defaultDateFormat") };
      } else if (item.key === "requested_on") {
        return {
          title: item.tableHeaderName,
          dataIndex: item.dataIndex,
          key: item.key,
          sorter: true,
          render: (value: any) => formatDisplayDate(value, "reminderDateActualFormat", "defaultDateFormat"),
        };
      } else if (item.key === "member_matching_status") {
        return {
          title: item.tableHeaderName,
          dataIndex: item.dataIndex,
          key: item.key,
          sorter: true,
          render: (value: any, record: any) => {
            value = cancelledPendingProcessingIds.includes(record?.member_unique_id) ? "Cancelled" : value;
            const _className = classNames("ui-expandable-table__status", {
              "ui-expandable-table__status__pending": value?.toLowerCase() === "pending",
              "ui-expandable-table__status__completed": value?.toLowerCase() === "completed",
            });
            return (
              <div className="ui-expandable-table__action-column">
                <div className={_className}> {value}</div>
                {value?.toLowerCase() === "pending" || value?.toLowerCase() === "failed" ? (
                  <Popconfirm
                    title="Are you sure to cancel this task?"
                    onConfirm={() => {
                      cancelOnGoingProcessing(record?.member_unique_id);
                    }}
                    icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                    okText={"Yes"}
                    cancelText={"No"}
                  >
                    <Tooltip title={"Cancel"}>
                      <CloseOutlined />
                    </Tooltip>
                  </Popconfirm>
                ): null }
              </div>
            );
          },
        };
      } else if (item.key === "action_description") {
        return {
          title: item.tableHeaderName,
          dataIndex: item.dataIndex,
          key: item.key,
          sorter: true,
          render: (value: any, record: any) => {
            return (
              <div className="ui-expandable-table__action-column">
                {record.action?.toLowerCase()?.includes("empi") ? (
                  <Tooltip title={"EMPI to EMPI Merge"}>
                    <div className="clr-s100">{value}</div>
                  </Tooltip>
                ) : record.action?.toLowerCase()?.includes("merge") ? (
                  <Tooltip title={"Intervention Merge"}>
                    <div className="clr-a100">{record.action}</div>
                  </Tooltip>
                ) : (
                  <div className="clr-p100">{record.action}</div>
                )}
                {value?.toLowerCase()?.includes("merg") ? (
                  <ExtraDetailsOfUserComparison record={record} columnListForPopUp={columnsNeedToShowInPopUp} />
                ) : (
                  <ExtraDetailsOfUser record={record} columnListForPopUp={columnsNeedToShowInPopUp} />
                )}
              </div>
            );
          },
        };
      } else {
        return { title: item.tableHeaderName, dataIndex: item.dataIndex, key: item.key, sorter: true, render: (value: any) => value || "--" };
      }
    });

    _columns = sortBy(_columns, (item: any) => {
      return item.order;
    });

    setColumnList(_columns);
  }, [columnsNeedToShowInTable, columnsNeedToShowInPopUp, cancelledPendingProcessingIds, cancelOnGoingProcessing, disableCancelButton]);

  useEffect(() => {
    WellBeEMPIService.getColumnsList({ pageName: "EMPI-Stewardship-Pending-Processing" }).subscribe({
      next: (v) => {
        setColumnsNeedToShowInTable(
          filter(v.data.data.columns, (item) => {
            return item.isDisplay === "Y";
          })
        );
        setColumnsNeedToShowInPopUp(
          filter(v.data.data.columns, (item) => {
            return item.isAdditionalInfo === "Y";
          })
        );
      },
      error: (e) => {
        console.log(e);
      },
    });
  }, []);

  const checkBatchProcessingStatus = () => {
    WellBeEMPIService.getPendingProcessingStatus().subscribe({
      next: (v) => {
        setLastRefreshDate(v?.data?.data?.statusRecord?.lastRefreshDate);
        setDisableCancelButton(v?.data?.data?.statusRecord?.status);
        setFromHour(v?.data?.data?.scheduleRecord?.refreshStartTime);
        setToHour(v?.data?.data?.scheduleRecord?.refreshEndTime);
        setDayOfBatchProcessing(parseInt(v?.data?.data?.scheduleRecord?.refreshDay));
      },
      error: (e) => {
        console.log(e);
      },
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      let currentDate = new Date();

      let startTime = fromHour;
      let endTime = toHour;

      let startDate: any = new Date(currentDate.getTime());
      startDate.setHours(startTime?.split(":")[0]);
      startDate.setMinutes(startTime?.split(":")[1]);
      startDate.setSeconds(startTime?.split(":")[2]);
      startDate = momentTZ.tz(startDate, "EST").local();

      let endDate: any = new Date(currentDate.getTime());
      endDate.setHours(endTime?.split(":")[0]);
      endDate.setMinutes(endTime?.split(":")[1]);
      endDate.setSeconds(endTime?.split(":")[2]);
      endDate = momentTZ.tz(endDate, "EST").local();

      if (currentDate.getDay() === dayOfBatchProcessing && startDate < currentDate && endDate > currentDate) {
        checkBatchProcessingStatus();
      }
    }, 1000 * 60 * 5); // 5 minutes in milliseconds

    return () => {
      // Clean up the interval when the component unmounts
      clearInterval(interval);
    };
  }, [dayOfBatchProcessing, fromHour, toHour]);

  let resultDate = new Date();
  resultDate.setDate(resultDate.getDate() + ((dayOfBatchProcessing + (7 - resultDate.getDay())) % 7));

  const _className = classNames("ui-expandable-table-pending-processing--footer__status", {
    "ui-expandable-table-pending-processing--footer__status__pending": disableCancelButton === "Pending",
    "ui-expandable-table-pending-processing--footer__status__completed": disableCancelButton?.includes("Comple"),
  });

  return (
    <>
      {disableCancelButton === "Processing" && (
        <div className="page-data-loading-information-status-wrapper">
          <div className="page-data-loading-information-status">
            <div className="page-data-loading-information-status__message">
              <span className="page-data-loading-information-status__message__loader">
                <Icon name={"sandTimerLoader"} />
              </span>
              <span className="page-data-loading-information-status__message__text">We Have freezed all of the actions because batch processing is going on.</span>
            </div>
          </div>
        </div>
      )}
      {/* <div className="ui-expandable-table-pending-processing__color-info">
        <div className="ui-expandable-table-pending-processing__color-info__section">
          <div className="ui-expandable-table-pending-processing__color-info__section__box"></div>
          <div>EMPI to EMPI Merge</div>
        </div>
        <div className="ui-expandable-table-pending-processing__color-info__section">
          <div className="ui-expandable-table-pending-processing__color-info__section__box ui-expandable-table-pending-processing__color-info__section__box--interventionMerge"></div>
          <div>Intervention Merge</div>
        </div>
      </div> */}

      <TableWithDynamicPagination
        condition={props.activeTab === "pending_processing"}
        dependencyProps={[props.activeTab]}
        className="ui-expandable-table"
        columns={columnList}
        handleFetchData={fetchData}
        loading={loading}
        perPageCount={10}
        columnOriginalList={columnsNeedToShowInTable}
      />

      {!loading && (
        <div className="ui-expandable-table-pending-processing--footer">
          <div>
            <b>Last Refresh Date:</b> {formatDisplayDate(lastRefreshDate, "createDateFormat", "defaultDateFormat", true)}
          </div>{" "}
          |
          <div>
            <b>Refresh Status:</b> <div className={_className}>{disableCancelButton}</div>
          </div>{" "}
          |
          <div>
            <b>Next refresh Date:</b> {formatDisplayDate(resultDate, "createDateFormat", "defaultDateFormat")}
          </div>
        </div>
      )}
    </>
  );
};

export default PendingProcessing;
